import type { Column } from "./columns/types";
import type { CriteriaFilterValue } from "./SteppedMultiColumnDropdown";

function resolveSelectionToName(selectedValue: string, column: Column | undefined): string {
  if (!selectedValue || !column) {
    return selectedValue;
  }

  if (
    (column.type === "checkbox" || column.type === "list" || column.type === "search") &&
    column.items
  ) {
    const item = column.items.find((entry) => entry.id === selectedValue);
    return item?.name ?? selectedValue;
  }

  return selectedValue;
}

/**
 * Converts selections array and active columns to CriteriaFilterValue
 */
export function convertSelectionsToCriteriaFilterValue(
  selections: string[],
  activeColumns: Column[],
): CriteriaFilterValue | null {
  if (selections.length === 0 || activeColumns.length === 0) {
    return null;
  }

  const criteria = selections[0];
  const isTagCriteria = criteria === "account-tag" || criteria === "resource-tag";

  // For tag criteria: [criteria, key, operator, value]
  // For regular criteria: [criteria, operator, value]
  let name: string;
  let operator: string;
  let valueString: string;
  let valueColumnIndex: number;

  if (isTagCriteria) {
    if (selections.length < 4) return null;
    const tagKeyColumn = activeColumns[1];
    name = resolveSelectionToName(selections[1], tagKeyColumn); // The tag key
    operator = selections[2];
    valueString = selections[3];
    valueColumnIndex = 3;
  } else {
    if (selections.length < 3) return null;
    name = criteria; // Same as criteria for non-tag types
    operator = selections[1];
    valueString = selections[2];
    valueColumnIndex = 2;
  }

  // Get the value column to look up items
  const valueColumn = activeColumns[valueColumnIndex];
  if (!valueColumn) return null;

  // Convert value string to array of {id, name} objects
  const value: { id: string; name: string }[] = [];

  if (valueColumn.type === "checkbox") {
    // Checkbox columns store comma-separated values
    const ids = valueString ? valueString.split(",").filter(Boolean) : [];
    for (const id of ids) {
      const resolvedName = resolveSelectionToName(id, valueColumn);
      value.push({ id, name: resolvedName });
    }
  } else if (valueColumn.type === "list" || valueColumn.type === "search") {
    // Single value for list/search columns
    if (valueString) {
      const resolvedName = resolveSelectionToName(valueString, valueColumn);
      value.push({ id: valueString, name: resolvedName });
    }
  } else if (valueColumn.type === "enter-value") {
    // Enter-value columns store the entered text directly
    if (valueString) {
      value.push({ id: valueString, name: valueString });
    }
  }

  return {
    criteria,
    name,
    operator,
    value,
  };
}
